interface EmbedConfig {
  host: string;
  widgetPath: string;
  targetId: string;
  type: string;
  channel?: string;
  lang: string;
  policyLink: string;
  ofertaLink: string;
  phonePlaceholder?: string;
  phonePrefix?: string;
}

const EMBED_STEPS = [
  'Initializing VMS-NC platform core...',
  'Connecting to glass and hardware catalog...',
  'Loading profile and seal parameters...',
  'Initializing 3D visualization module...',
  'Retrieving price matrix and services...',
  'Building calculator workspace...',
];

const EMBED_TRANSLATIONS: Record<string, Record<string, string>> = {
  ru: {
    'Loading modules': 'Загрузка модулей',
    'Initializing VMS-NC platform core...': 'Инициализация ядра платформы VMS-NC...',
    'Connecting to glass and hardware catalog...': 'Подключение к каталогу стекол и фурнитуры...',
    'Loading profile and seal parameters...': 'Загрузка параметров профилей и уплотнителей...',
    'Initializing 3D visualization module...': 'Инициализация 3D-модуля визуализации...',
    'Retrieving price matrix and services...': 'Получение матрицы цен и услуг...',
    'Building calculator workspace...': 'Построение рабочей области калькулятора...',
  },
  pl: {
    'Loading modules': 'Ładowanie modułów',
    'Initializing VMS-NC platform core...': 'Inicjalizacja rdzenia platformy VMS-NC...',
    'Connecting to glass and hardware catalog...': 'Łączenie z katalogiem szkła i okuć...',
    'Loading profile and seal parameters...': 'Ładowanie parametrów profili i uszczelek...',
    'Initializing 3D visualization module...': 'Inicjalizacja modułu wizualizacji 3D...',
    'Retrieving price matrix and services...': 'Pobieranie tabeli cen i usług...',
    'Building calculator workspace...': 'Budowanie obszaru roboczego kalkulatora...',
  },
};

const t = (key: string, lang = 'ru'): string => {
  if (lang === 'en') return key;
  return EMBED_TRANSLATIONS[lang]?.[key] || key;
};

(function () {
  const scriptEl =
    (document.currentScript as HTMLScriptElement) ||
    document.querySelector<HTMLScriptElement>('script[src*="embed.js"]');

  if (!scriptEl) {
    console.error('[VMS Embed] Скрипт embed.js не найден в DOM');
    return;
  }

  const scriptUrl = new URL(scriptEl.src, window.location.href);
  const host = scriptUrl.origin;
  const lastSlashIndex = scriptUrl.pathname.lastIndexOf('/');
  const widgetPath = lastSlashIndex > 0 ? scriptUrl.pathname.substring(0, lastSlashIndex) : '/widget';

  const config: EmbedConfig = {
    host,
    widgetPath,
    targetId: scriptEl.dataset.target || 'calcAppRoot',
    type: scriptEl.dataset.type || 'user',
    channel: scriptEl.dataset.channel,
    lang: scriptEl.dataset.lang || 'ru',
    policyLink: scriptEl.dataset.policyLink || '/privacy',
    ofertaLink: scriptEl.dataset.ofertaLink || '/offer',
    phonePlaceholder: scriptEl.dataset.phonePlaceholder,
    phonePrefix: scriptEl.dataset.phonePrefix,
  };

  (window as any).__CALC_ASSET_BASE_URL__ = `${host}${widgetPath}/`;

  let targetContainer = document.getElementById(config.targetId);
  if (!targetContainer) {
    targetContainer = document.createElement('div');
    targetContainer.id = config.targetId;
    scriptEl.parentNode?.insertBefore(targetContainer, scriptEl);
  }

  injectHeadStyles(config.targetId);

  const loaderEl = createLoaderElement(config.lang);
  targetContainer.style.position = 'relative';
  targetContainer.style.minHeight = '650px';
  targetContainer.appendChild(loaderEl);

  boot(config, loaderEl);

  function injectHeadStyles(targetId: string): void {
    const head = document.head;

    if (!document.querySelector('link[href*="DM+Sans"]')) {
      const preconnect = document.createElement('link');
      preconnect.rel = 'preconnect';
      preconnect.href = 'https://fonts.googleapis.com';
      head.appendChild(preconnect);

      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href =
        'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap';
      head.appendChild(fontLink);
    }

    const styleId = 'vms-embed-reset-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        :where(#${targetId}, #${targetId} *, #${targetId} *::before, #${targetId} *::after) {
          box-sizing: border-box;
          border-width: 0;
          border-style: solid;
          border-color: #E8EBEF;
        }

        #${targetId} {
          font-family: "DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
          font-size: 16px;
          line-height: 1.5;
          color: #16191B;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        #${targetId} * {
          font-family: inherit;
        }

        #${targetId} input[type="number"]::-webkit-inner-spin-button,
        #${targetId} input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        #${targetId} input[type="number"] {
          -moz-appearance: textfield;
        }

        #${targetId} button {
          cursor: pointer;
        }

        @keyframes vms-spin-anim {
          to { transform: rotate(360deg); }
        }
      `;
      head.appendChild(style);
    }
  }

  function createLoaderElement(lang: string = 'ru'): HTMLElement {
    const loader = document.createElement('div');
    loader.id = 'vms-widget-preloader';
    loader.style.cssText = `
      position: absolute; inset: 0; display: flex; flex-direction: column;
      align-items: center; justify-content: center; background: #ffffff;
      z-index: 40; gap: 16px; color: #4b5563; font-family: "DM Sans", sans-serif;
      font-size: 15px; border-radius: 16px; pointer-events: none;
      transition: opacity 0.35s ease-out;
    `;

    const dotsHtml = EMBED_STEPS.map((_, i) =>
      `<div class="vms-p-dot" style="height: 4px; width: ${i === 0 ? '24px' : '6px'}; background: ${i === 0 ? '#16191B' : '#E8EBEF'}; border-radius: 9999px; transition: all 0.3s;"></div>`
    ).join('');

    loader.innerHTML = `
      <div style="position: relative; width: 44px; height: 44px; margin-bottom: 8px;">
        <div style="position: absolute; inset: 0; border: 1.5px dashed #E8EBEF; border-radius: 50%;"></div>
        <div style="position: absolute; inset: 0; border: 2px solid #16191B; border-top-color: transparent; border-right-color: transparent; border-bottom-color: transparent; border-radius: 50%; animation: vms-spin-anim 0.9s linear infinite;"></div>
      </div>
      <div style="text-align: center; max-width: 400px;">
        <div id="vms-loader-title" style="font-weight: 600; font-size: 16px; color: #16191B; letter-spacing: -0.01em; margin-bottom: 6px;">${t('Loading modules', lang)}</div>
        <p id="vms-loader-step-text" style="margin: 0; font-size: 14px; font-weight: 500; color: #728499; transition: opacity 0.2s;">
          ${t(EMBED_STEPS[0], lang)}
        </p>
      </div>
      <div id="vms-loader-dots" style="display: flex; gap: 6px; margin-top: 12px;">
        ${dotsHtml}
      </div>
    `;

    return loader;
  }

  async function boot(cfg: EmbedConfig, loader: HTMLElement): Promise<void> {
    try {
      const manifestRes = await fetch(`${cfg.host}${cfg.widgetPath}/manifest.json?_t=${Date.now()}`);
      if (!manifestRes.ok) {
        throw new Error(`HTTP ${manifestRes.status} при загрузке manifest.json`);
      }

      const manifest = await manifestRes.json();
      const mainJs = manifest['main.js'];
      const mainCss = manifest['main.css'];

      if (!mainJs) {
        throw new Error('main.js не найден в manifest.json');
      }

      const jsUrl = `${cfg.host}${cfg.widgetPath}/${mainJs}`;
      const cssUrl = mainCss ? `${cfg.host}${cfg.widgetPath}/${mainCss}` : null;

      if (cssUrl && !document.querySelector(`link[href="${cssUrl}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;
        document.head.appendChild(link);
      }

      const script = document.createElement('script');
      script.type = 'module';
      script.crossOrigin = 'anonymous';
      script.src = jsUrl;

      script.onload = () => {
        if (typeof (window as any).initCalculator === 'function') {
          try {
            const existingInitialData = (window as any).initialData || {};

            const finalData = {
              apiUrl: `${cfg.host}/api/v1`,
              assetsUrl: `${cfg.host}${cfg.widgetPath}/`,
              baseUrl: cfg.host,
              policyLink: cfg.policyLink,
              ofertaLink: cfg.ofertaLink,
              phonePlaceholder: cfg.phonePlaceholder,
              phonePrefix: cfg.phonePrefix,
              state: null,
              type: cfg.type,
              channel: cfg.channel,
              lang: cfg.lang,
              user: null,
              auth: {client: null, employee: null},
              ...existingInitialData,
            };

            (window as any).initCalculator(cfg.targetId, finalData);

            loader.style.opacity = '0';
            loader.style.transition = 'opacity 0.25s ease';
            setTimeout(() => loader.remove(), 250);
          } catch (initErr) {
            console.error('[VMS Embed] Ошибка выполнения initCalculator:', initErr);
            loader.remove();
          }
        } else {
          console.error('[VMS Embed] window.initCalculator не найден в модуле');
          loader.remove();
        }
      };

      script.onerror = () => {
        console.error('[VMS Embed] Не удалось загрузить JS бандл:', jsUrl);
        loader.remove();
      };

      document.body.appendChild(script);
    } catch (err) {
      console.error('[VMS Embed] Ошибка инициализации:', err);
      loader.remove();
    }
  }
})();